import type { PerfilDoPlayer1, PerfilEnviado, PerfisDoPlayer1 } from '../../conteudo/api/conteudo';
import { LIXEIRA } from './comum';
import { conferirPerfis } from './conferirPerfis';

/** Um perfil como está na tela: "De" e "Até" como digitados (vazio é ponta aberta). */
export interface PerfilEmEdicao {
    chave: string;
    id: string | null;
    titulo: string;
    texto: string;
    de: string;
    ate: string;
}

const limite = (valor: number | null) => (valor === null ? '' : String(valor));
const numero = (digitado: string) => (digitado.trim() === '' ? null : Number(digitado));

export function perfisEmEdicao(perfis: PerfilDoPlayer1[]): PerfilEmEdicao[] {
    return perfis.map((perfil) => ({
        chave: perfil.id,
        id: perfil.id,
        titulo: perfil.titulo,
        texto: perfil.texto,
        de: limite(perfil.minimo),
        ate: limite(perfil.maximo),
    }));
}

export function perfisParaEnviar(perfis: PerfilEmEdicao[]): PerfilEnviado[] {
    return perfis.map((perfil) => ({
        id: perfil.id,
        titulo: perfil.titulo,
        texto: perfil.texto,
        minimo: numero(perfil.de),
        maximo: numero(perfil.ate),
    }));
}

/** O que falta organizar na tabela da tela, ou `null` quando ela pode ser gravada. */
export function problemaDosPerfis(dados: PerfisDoPlayer1, perfis: PerfilEmEdicao[]): string | null {
    return conferirPerfis(perfisParaEnviar(perfis), dados.soma_minima, dados.soma_maxima);
}

/**
 * O cartão "Perfis" do Player 1 (`RA13c`): o perfil do participante é o que contém a soma dos
 * quatro indicadores no fim da jornada. O admin inclui, exclui e edita à vontade; enquanto a
 * tabela tiver buraco, sobreposição ou soma possível sem perfil, o aviso fica aqui e o Salvar do
 * rodapé não grava. O conjunto vai ao painel de uma vez só.
 */
export function PerfisDoPlayer1Cartao({ dados, perfis, aoMudar, travado }: {
    dados: PerfisDoPlayer1;
    perfis: PerfilEmEdicao[];
    aoMudar: (perfis: PerfilEmEdicao[]) => void;
    travado: boolean;
}) {
    const problema = problemaDosPerfis(dados, perfis);
    const mudar = (chave: string, mudanca: Partial<PerfilEmEdicao>) =>
        aoMudar(perfis.map((perfil) => (perfil.chave === chave ? { ...perfil, ...mudanca } : perfil)));
    const excluir = (perfil: PerfilEmEdicao) => {
        if (perfil.id && !window.confirm(`Excluir o perfil «${perfil.titulo}»? Ele sai da tabela quando você salvar.`)) return;
        aoMudar(perfis.filter((outro) => outro.chave !== perfil.chave));
    };

    return (
        <section className="cartao-config" aria-labelledby="player1-perfis">
            <div className="cabecalho-cartao-config">
                <h3 id="player1-perfis">Perfis</h3>
                <span className="nota-config">Somas possíveis: de {dados.soma_minima} a {dados.soma_maxima}</span>
            </div>
            <p className="nota-config">
                O perfil é o que contém a soma dos quatro indicadores no fim da jornada. No texto, o 1º parágrafo é a
                descrição na tela de resultado e os demais são a mensagem do pop-up final. Mudar os perfis também muda o
                perfil que o relatório mostra para as turmas que já jogaram.
            </p>

            {perfis.map((perfil, i) => {
                const n = i + 1;
                const titulo = `player1-perfil-${perfil.chave}`;
                return (
                    <section key={perfil.chave} className="cartao-pista" aria-labelledby={titulo}>
                        <div className="cabecalho-cartao-config">
                            <h4 id={titulo}>Perfil {n}</h4>
                            {!travado && (
                                <button type="button" className="lixeira-config" aria-label={`Excluir Perfil ${n}`} onClick={() => excluir(perfil)}>
                                    {LIXEIRA}
                                </button>
                            )}
                        </div>
                        <div className="linha-perfil-config">
                            <label className="campo-config">
                                Título
                                <input type="text" value={perfil.titulo} disabled={travado} onChange={(evento) => mudar(perfil.chave, { titulo: evento.target.value })} />
                            </label>
                            <label className="campo-config campo-config-curto">
                                De
                                <input type="number" step={1} value={perfil.de} disabled={travado} onChange={(evento) => mudar(perfil.chave, { de: evento.target.value })} />
                            </label>
                            <label className="campo-config campo-config-curto">
                                Até
                                <input type="number" step={1} value={perfil.ate} disabled={travado} onChange={(evento) => mudar(perfil.chave, { ate: evento.target.value })} />
                            </label>
                        </div>
                        <label className="campo-config">
                            Texto
                            <textarea rows={4} value={perfil.texto} disabled={travado} onChange={(evento) => mudar(perfil.chave, { texto: evento.target.value })} />
                        </label>
                    </section>
                );
            })}

            {problema && <p className="mensagem-login aviso-cadastro" role="alert">{problema}</p>}

            <div>
                <button
                    type="button"
                    className="botao-novo-config"
                    disabled={travado}
                    onClick={() => aoMudar([
                        ...perfis,
                        { chave: `novo-${Date.now()}-${perfis.length}`, id: null, titulo: '', texto: '', de: '', ate: '' },
                    ])}
                >
                    <span aria-hidden="true">+</span> Novo Perfil
                </button>
            </div>
        </section>
    );
}
